import {Pipe, PipeTransform} from '@angular/core';
import {Ttm} from '../classes/ttm';
import * as turf from '@turf/turf';
import {Feature, Point, Polygon} from 'geojson';
import {Entity} from '../classes/entity';
import {TtmStatus} from '../classes/ttmstatus';

@Pipe({
    name: 'clusterTtms'
})
export class ClusterTtmsPipe implements PipeTransform {

    transform(ttms: Ttm[], zoom: number, ttmStatuses: TtmStatus[]): {
        clusterPolygons: Feature<Polygon>[],
        colorGroup: number,
        color: string,
    }[] {
        const clusteringRadius = .1;
        const zoomBufferMap = new Map<number, number>([
            [18, 0.10], [17, 0.10], [16, 0.25], [15, 0.5], [14, 1], [13, 2], [12, 3], [11, 3], [10, 4], [9, 4], [8, 4], [7, 5], [6, 6], [5, 7],
        ]);

        const ttmStatusMap = new Map<string, TtmStatus>();
        ttmStatuses.forEach(s => ttmStatusMap.set(s.key, s));
        const colorGroups = Array.from(new Set(ttms.map(t => ttmStatusMap.get(t.status_key).color_group)));
        const groupColorMap = new Map<number, string>();
        colorGroups.forEach(colorGroup => {
            groupColorMap.set(colorGroup, this.averageColor(ttmStatuses.filter(s => s.color_group === colorGroup)));
        });

        const clusterPolygonsWithStatus: {
            clusterPolygons: Feature<Polygon>[],
            colorGroup: number,
            color: string
        }[] = [];

        colorGroups.forEach(colorGroup => {
            const entities = ttms.filter(ttm => ttmStatusMap.get(ttm.status_key).color_group === colorGroup).map(t => t.entities).flat();
            const uniqueDrawingIds = new Set(entities.map(e => e.drawing_ids).flat());
            const polygonsForStatus: Feature<Polygon>[] = [];

            uniqueDrawingIds.forEach(drawingId => {
                const features: Feature<Point>[] = entities.filter(e => e.drawing_ids.includes(drawingId))
                    .map(this.convertEntityToFeature)
                    .filter((feature): feature is Feature<Point> => feature !== null);

                const featureCollection = turf.featureCollection(features);
                // Cluster de punten
                const clustered = turf.clustersDbscan(featureCollection, clusteringRadius, {units: 'kilometers', minPoints: 1});

                const clusters: Record<number, Feature<Point>[]> = {};
                clustered.features.forEach(feature => {
                    const clusterId = feature.properties?.cluster;
                    if (clusterId !== undefined) {
                        if (!clusters[clusterId]) {
                            clusters[clusterId] = [];
                        }
                        clusters[clusterId].push(feature);
                    }
                });


                Object.values(clusters).forEach(clusterPoints => {
                    if (clusterPoints.length >= 3) { // Minimaal 3 punten nodig voor een polygon
                        const clusterFeatureCollection = turf.featureCollection(clusterPoints);
                        const clusterPolygon = turf.convex(clusterFeatureCollection);
                        if (clusterPolygon) {
                            clusterPolygon.properties = {cluster: (clusterPoints[0].properties as any)?.cluster}; // Cluster-ID opslaan
                            polygonsForStatus.push(turf.buffer(clusterPolygon, zoomBufferMap.get(zoom) ?? 0.5, {units: 'kilometers'}) as any);
                        }
                    } else {
                        clusterPoints.forEach(point => {
                            polygonsForStatus.push(turf.buffer(point, (zoomBufferMap.get(zoom) ?? 0.5) * 0.25, {units: 'kilometers'}) as any);
                        });
                    }
                });
            });
            const polygonsForThisStatus = {
                clusterPolygons: [],
                colorGroup: colorGroups.length > 1 ? colorGroup : 10,
                color: groupColorMap.get(colorGroup),
            };
            if (polygonsForStatus.length > 1) {
                const union = turf.union(turf.featureCollection(polygonsForStatus));
                if (union.geometry.type === 'Polygon') {
                    polygonsForThisStatus.clusterPolygons.push(union as any);
                } else {
                    polygonsForThisStatus.clusterPolygons.push(...union.geometry.coordinates.map(c => turf.polygon(c)));
                }
            } else {
                polygonsForThisStatus.clusterPolygons.push(...polygonsForStatus);
            }
            polygonsForThisStatus.clusterPolygons = polygonsForThisStatus.clusterPolygons.map(h => h.geometry)
            clusterPolygonsWithStatus.push(polygonsForThisStatus);
        });

        return clusterPolygonsWithStatus;
    }

    convertEntityToFeature(entity: Entity): Feature<Point> | null {
        if (entity.gnd && Array.isArray(entity.gnd.coordinates)) {
            return turf.point(entity.gnd.coordinates[0] as any, {id: entity.id});
        }
        return null;
    }

    averageColor(ttmStatuses: TtmStatus[]) {
        function hexToRgb(hex: string): [number, number, number] {
            const cleanHex = hex.replace('#', '');
            const r = parseInt(cleanHex.substring(0, 2), 16);
            const g = parseInt(cleanHex.substring(2, 4), 16);
            const b = parseInt(cleanHex.substring(4, 6), 16);
            return [r, g, b];
        }

        function rgbToHex(r: number, g: number, b: number): string {
            return (
                '#' +
                [r, g, b]
                    .map(x => x.toString(16).padStart(2, '0'))
                    .join('')
            );
        }

        function averageColor(hexColors: string[]): string {
            let totalR = 0, totalG = 0, totalB = 0;

            hexColors.forEach(hex => {
                const [r, g, b] = hexToRgb(hex);
                totalR += r;
                totalG += g;
                totalB += b;
            });

            const count = hexColors.length;
            const avgR = Math.round(totalR / count);
            const avgG = Math.round(totalG / count);
            const avgB = Math.round(totalB / count);

            return rgbToHex(avgR, avgG, avgB);
        }

        return averageColor(ttmStatuses.map(s => s.color));
    }

}
